import api from "@rapid/utils/api";
import type { Api } from "@rapid/utils/api";
import { useState, useCallback, useRef, useEffect } from "react";

const useApi = (): Api => api;
export default useApi;

// Re-export error classes for backward compatibility
export { APIRequestError } from "@rapid/utils/api/errors";

/**
 * useAsyncBoundOperation is a hook that provides centralized state management for async operations/promises with abort support.
 * Manages loading state, error handling, and results for async callbacks that can be cancelled.
 * The operation can be auto-invoked on mount or manually triggered
 */
type InferAsyncReturn<T extends (...args: any[]) => Promise<any>> = Awaited<ReturnType<T>>;
export const useAsyncBoundOperation = <
    OPERATION extends (abortController: AbortController) => Promise<T>,
    T = InferAsyncReturn<OPERATION>
>(
    asyncOperation: OPERATION,
    options?: { autoInvoke?: boolean }
) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [result, setResult] = useState<T | undefined>(undefined);
    const abortControllerRef = useRef<AbortController | null>(null);
    const hasAutoInvokedRef = useRef(false);

    const { autoInvoke = false } = options || {};

    const reset = useCallback(() => {
        setIsLoading(false);
        setError(null);
        setResult(undefined);
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    const invoke = useCallback(async (): Promise<T | null> => {
        // Abort any previous operation
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setIsLoading(true);
        setError(null);

        try {
            const operationResult = await asyncOperation(abortController);

            // Only update state if not aborted
            if (!abortController.signal.aborted) {
                setResult(operationResult);
                return operationResult;
            }
            return null;
        } catch (err: any) {
            // Don't set error if operation was aborted
            if (err.name === "AbortError" && abortController.signal.aborted) {
                return null;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
            setResult(undefined);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [asyncOperation]);

    // Auto-invoke when operation signature changes, but only once per hook instance
    useEffect(() => {
        if (autoInvoke && !hasAutoInvokedRef.current) {
            hasAutoInvokedRef.current = true;
            invoke();
        }

        // Cleanup: abort on unmount or operation change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            // Reset hasAutoInvoked when the operation changes
            hasAutoInvokedRef.current = false;
        };
    }, [invoke, autoInvoke]);

    return { isLoading, error, result, reset, invoke };
};
